import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  archiveExpense, deleteExpense,
  listExpenses, listCategories, listVendors, stopExpense, reactivateExpense,
  unarchiveExpense,
} from "@/lib/api";
import { formatApiError } from "@/lib/api-error";
import { EUR, fmtDate, recurrenceIT } from "@/lib/format";
import { ExportButtons } from "@/components/ExportButtons";
import { buildFilename } from "@/lib/export";
import { SortableHeader, makeSorter } from "@/components/SortableHeader";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Search, X } from "lucide-react";

export default function Expenses() {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState({});
  const [vens, setVens] = useState({});
  const [loading, setLoading] = useState(true);
  const [stopTarget, setStopTarget] = useState(null);
  const [sort, setSort] = useState({ field: "title", dir: "asc" });
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("active"); // active | inactive | all
  const [recurrenceFilter, setRecurrenceFilter] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    const [exp, c, v] = await Promise.all([
      listExpenses({ is_ordinary: false, include_archived: showArchived }),
      listCategories(),
      listVendors(),
    ]);
    setRows(exp || []);
    setCats(Object.fromEntries((c || []).map((x) => [x.id, x])));
    setVens(Object.fromEntries((v || []).map((x) => [x.id, x])));
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [showArchived]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter === "active" && !r.is_active) return false;
      if (statusFilter === "inactive" && r.is_active) return false;
      if (recurrenceFilter !== "all" && r.recurrence_type !== recurrenceFilter) return false;
      if (!q) return true;
      const haystack = [
        r.title,
        r.description,
        cats[r.category_id]?.name,
        vens[r.vendor_id]?.name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, query, statusFilter, recurrenceFilter, cats, vens]);

  const sortedRows = useMemo(() => {
    const list = [...filteredRows];
    const sorter = makeSorter(sort, (r, field) => {
      switch (field) {
        case "title":
          return (r.title || "").toLowerCase();
        case "recurrence_type":
          return recurrenceIT[r.recurrence_type] || "";
        case "category":
          return cats[r.category_id]?.name || "";
        case "vendor":
          return vens[r.vendor_id]?.name || "";
        case "start_date":
          return r.start_date;
        case "planned_amount":
          return Number(r.planned_amount || 0);
        case "is_active":
          return r.is_active ? 0 : 1; // active first when asc
        default:
          return null;
      }
    });
    if (sorter) list.sort(sorter);
    return list;
  }, [filteredRows, sort, cats, vens]);

  const doStop = async () => {
    try {
      await stopExpense(stopTarget.id);
      toast.success("Serie interrotta");
      setStopTarget(null);
      load();
    } catch (e) {
      toast.error("Errore");
    }
  };

  const doReactivate = async (r) => {
    try {
      await reactivateExpense(r.id);
      toast.success("Serie riattivata");
      load();
    } catch (e) {
      toast.error("Errore");
    }
  };

  return (
    <div data-testid="expenses-page">
      <PageHeader
        title="Spese ricorrenti"
        subtitle="Definizioni delle serie ricorrenti. Ogni serie genera le sue scadenze pianificate."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ExportButtons
              rows={rows.map((r) => ({
                title: r.title,
                description: r.description || "",
                recurrence_type: recurrenceIT[r.recurrence_type] || r.recurrence_type,
                category: cats[r.category_id]?.name || "",
                vendor: vens[r.vendor_id]?.name || "",
                start_date: r.start_date,
                end_date: r.end_date || "",
                planned_amount: r.planned_amount,
                is_active: r.is_active ? "Attiva" : "Interrotta",
                stopped_at: r.stopped_at || "",
              }))}
              columns={[
                { key: "title", header: "Titolo" },
                { key: "description", header: "Descrizione" },
                { key: "recurrence_type", header: "Ricorrenza" },
                { key: "category", header: "Categoria" },
                { key: "vendor", header: "Fornitore" },
                { key: "start_date", header: "Decorrenza", type: "date" },
                { key: "end_date", header: "Scadenza serie", type: "date" },
                { key: "planned_amount", header: "Importo (€)", type: "currency" },
                { key: "is_active", header: "Stato" },
                { key: "stopped_at", header: "Interrotta il", type: "date" },
              ]}
              filenameBase={buildFilename("spese_ricorrenti")}
              sheetName="Spese ricorrenti"
              testId="export-expenses"
            />
            <Button asChild data-testid="btn-create-expense">
              <Link to="/expenses/new">+ Nuova scadenza ricorrente</Link>
            </Button>
          </div>
        }
      />

      <div
        data-testid="expenses-filters"
        className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-stone-200 bg-white p-3"
      >
        <div className="relative min-w-[260px] flex-1">
          <Search
            size={14}
            strokeWidth={1.8}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400"
          />
          <Input
            data-testid="filter-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca per titolo, descrizione, categoria o fornitore…"
            className="pl-8 pr-8"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              data-testid="filter-search-clear"
              aria-label="Pulisci"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="filter-status" className="h-9 w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte</SelectItem>
            <SelectItem value="active">Attive</SelectItem>
            <SelectItem value="inactive">Interrotte</SelectItem>
          </SelectContent>
        </Select>
        <Select value={recurrenceFilter} onValueChange={setRecurrenceFilter}>
          <SelectTrigger data-testid="filter-recurrence" className="h-9 w-[160px]">
            <SelectValue placeholder="Ricorrenza" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le ricorrenze</SelectItem>
            {Object.keys(recurrenceIT).map((k) => (
              <SelectItem key={k} value={k}>{recurrenceIT[k]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="ml-3 flex cursor-pointer items-center gap-1.5 text-[11px] text-stone-600">
          <input
            type="checkbox"
            data-testid="toggle-show-archived"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          Mostra archiviati / interrotti
        </label>
        <span
          data-testid="filter-result-count"
          className="ml-auto text-[11px] uppercase tracking-wider text-stone-500"
        >
          {filteredRows.length} di {rows.length}
        </span>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <SortableHeader field="title" sort={sort} onChange={setSort}>
                  Titolo
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader field="recurrence_type" sort={sort} onChange={setSort}>
                  Ricorrenza
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader field="category" sort={sort} onChange={setSort}>
                  Categoria
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader field="vendor" sort={sort} onChange={setSort}>
                  Fornitore
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader field="start_date" sort={sort} onChange={setSort}>
                  Decorrenza
                </SortableHeader>
              </TableHead>
              <TableHead className="text-right">
                <div className="flex justify-end">
                  <SortableHeader field="planned_amount" sort={sort} onChange={setSort}>
                    Importo
                  </SortableHeader>
                </div>
              </TableHead>
              <TableHead>
                <SortableHeader field="is_active" sort={sort} onChange={setSort}>
                  Stato
                </SortableHeader>
              </TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8} className="py-10 text-center text-sm text-stone-500">
                  Caricamento…
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-12 text-center">
                  <p className="text-sm text-stone-500">Nessuna serie ricorrente ancora definita.</p>
                  <Button asChild className="mt-4" data-testid="btn-empty-create">
                    <Link to="/expenses/new">Crea la prima serie ricorrente</Link>
                  </Button>
                </TableCell>
              </TableRow>
            )}
            {sortedRows.map((r) => (
              <TableRow key={r.id} data-testid={`expense-row-${r.id}`}>
                <TableCell>
                  <div className="font-medium text-stone-900">{r.title}</div>
                  {r.description && (
                    <div className="text-xs text-stone-500">{r.description}</div>
                  )}
                </TableCell>
                <TableCell className="text-sm">{recurrenceIT[r.recurrence_type]}</TableCell>
                <TableCell className="text-sm">{cats[r.category_id]?.name || "—"}</TableCell>
                <TableCell className="text-sm">{vens[r.vendor_id]?.name || "—"}</TableCell>
                <TableCell className="text-sm">{fmtDate(r.start_date)}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {EUR(r.planned_amount)}
                </TableCell>
                <TableCell>
                  {r.archived_at ? (
                    <Badge className="bg-stone-100 text-stone-600 hover:bg-stone-100 border border-stone-200">
                      Archiviata
                    </Badge>
                  ) : r.is_active ? (
                    <Badge className="bg-emerald-50 text-emerald-800 hover:bg-emerald-50 border border-emerald-200">
                      Attiva
                    </Badge>
                  ) : (
                    <Badge className="bg-stone-100 text-stone-600 hover:bg-stone-100 border border-stone-200">
                      Interrotta {r.stopped_at ? `• ${fmtDate(r.stopped_at)}` : ""}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1.5">
                    <Button
                      asChild
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-xs"
                      data-testid={`btn-edit-expense-${r.id}`}
                    >
                      <Link to={`/expenses/${r.id}/edit`}>Modifica</Link>
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          data-testid={`btn-actions-expense-${r.id}`}
                          title="Altre azioni"
                        >
                          <MoreHorizontal size={14} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="text-xs">
                        <DropdownMenuItem asChild>
                          <Link
                            to={`/expenses/new?from_expense=${r.id}`}
                            data-testid={`btn-duplicate-expense-${r.id}`}
                          >
                            Duplica serie
                          </Link>
                        </DropdownMenuItem>
                        {r.is_active && !r.archived_at && (
                          <DropdownMenuItem
                            data-testid={`btn-stop-expense-${r.id}`}
                            onClick={() => setStopTarget(r)}
                          >
                            Interrompi serie
                          </DropdownMenuItem>
                        )}
                        {!r.is_active && !r.archived_at && (
                          <DropdownMenuItem
                            data-testid={`btn-reactivate-expense-${r.id}`}
                            onClick={() => doReactivate(r)}
                          >
                            Riattiva serie
                          </DropdownMenuItem>
                        )}
                        {r.archived_at ? (
                          <DropdownMenuItem
                            data-testid={`btn-unarchive-expense-${r.id}`}
                            onClick={async () => {
                              try {
                                await unarchiveExpense(r.id);
                                toast.success("Spesa ripristinata.");
                                load();
                              } catch (e) {
                                toast.error(formatApiError(e, "Errore"));
                              }
                            }}
                          >
                            Ripristina da archivio
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            data-testid={`btn-archive-expense-${r.id}`}
                            onClick={async () => {
                              try {
                                await archiveExpense(r.id);
                                toast.success("Spesa archiviata.");
                                load();
                              } catch (e) {
                                toast.error(formatApiError(e, "Errore"));
                              }
                            }}
                          >
                            Archivia
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          data-testid={`btn-delete-expense-${r.id}`}
                          onClick={() => setConfirmDelete(r)}
                          className="text-rose-700 focus:text-rose-800"
                        >
                          Elimina
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!stopTarget} onOpenChange={(o) => !o && setStopTarget(null)}>
        <AlertDialogContent data-testid="dialog-stop-expense">
          <AlertDialogHeader>
            <AlertDialogTitle>Interrompere la serie?</AlertDialogTitle>
            <AlertDialogDescription>
              Le scadenze future <strong>non modificate e non pagate</strong> verranno rimosse.
              Le scadenze passate, pagate o modificate singolarmente vengono conservate come storico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="btn-confirm-stop" onClick={doStop}>
              Interrompi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="dialog-delete-expense">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare definitivamente la serie?</AlertDialogTitle>
            <AlertDialogDescription>
              L'eliminazione è permessa solo se la serie è ancora{" "}
              <strong>vergine</strong>: nessuna scadenza generata, nessun
              pagamento registrato. Altrimenti il backend rifiuterà
              l'operazione e dovrai usare <em>Archivia</em> o{" "}
              <em>Interrompi</em>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="btn-confirm-delete-expense"
              onClick={async () => {
                try {
                  await deleteExpense(confirmDelete.id);
                  toast.success("Spesa eliminata definitivamente.");
                  setConfirmDelete(null);
                  load();
                } catch (e) {
                  toast.error(formatApiError(e, "Impossibile eliminare."));
                  setConfirmDelete(null);
                }
              }}
            >
              Elimina definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
